// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
// Status icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
// Icons for check types
import RouterIcon from '@mui/icons-material/Router';
import SensorsIcon from '@mui/icons-material/Sensors';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import StorageIcon from '@mui/icons-material/Storage';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Badge,
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

const SEVERITY_ORDER = ['Unknown', 'Success', 'Warning', 'Error', 'Critical'];

const CHECK_ICON_MAP = {
  SensorHealthCheck: { label: 'Sensors', Icon: SensorsIcon },
  GatewayHealthCheck: { label: 'Gateway', Icon: RouterIcon },
  DataHealthCheck: { label: 'Data', Icon: StorageIcon },
  ConfigurationHealthCheck: {
    label: 'Configuration',
    Icon: SettingsRoundedIcon,
  },
  InstallationHealthCheck: { label: 'Installation', Icon: HandymanRoundedIcon },
  // add more mappings here if you later add checks
};

function normalizeStatus(raw) {
  if (!raw || typeof raw !== 'string') return 'Unknown';
  const s = raw.trim().toLowerCase();
  if (s.startsWith('succ')) return 'Success';
  if (s.startsWith('warn')) return 'Warning';
  if (s === 'error' || s === 'err') return 'Error';
  if (s.startsWith('crit')) return 'Critical';
  if (s.startsWith('unk')) return 'Unknown';
  return 'Unknown';
}

function worstStatus(statuses) {
  let worst = 'Unknown';
  for (const s of statuses) {
    const n = normalizeStatus(s);
    if (SEVERITY_ORDER.indexOf(n) > SEVERITY_ORDER.indexOf(worst)) {
      worst = n;
    }
  }
  return worst;
}

function getStatusIcon(status) {
  switch (status) {
    case 'Success':
      return { Color: 'success', StatusIcon: CheckCircleRoundedIcon };
    case 'Warning':
      return { Color: 'warning', StatusIcon: WarningRoundedIcon };
    case 'Error':
      return { Color: 'error', StatusIcon: ErrorRoundedIcon };
    case 'Critical':
      return { Color: 'error', StatusIcon: PriorityHighRoundedIcon };
    default:
      return { Color: 'default', StatusIcon: HelpOutlineRoundedIcon };
  }
}

/** Extract a check object by Name */
function getCheckByName(qos, name) {
  if (!qos?.HealthChecks || !Array.isArray(qos.HealthChecks)) return null;
  return qos.HealthChecks.find((c) => c?.Name === name) ?? null;
}

/** Flatten messages (warnings + errors) as displayable rows WITH type info */
function collectMessages(check) {
  if (!check) return [];

  const toText = (m) =>
    typeof m?.message === 'string' ? m.message : JSON.stringify(m);

  const humanizeType = (t) =>
    (t || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split camel/pascal
      .replace(/_/g, ' ') // underscores to spaces
      .trim();

  const warnings = (Array.isArray(check?.Warnings) ? check.Warnings : []).map(
    (m) => ({
      severity: 'Warning',
      text: toText(m),
      type: m?.warning_type || 'Warning',
      typeLabel: humanizeType(m?.warning_type || 'Warning'),
      deviceIds: m?.device_ids ?? [],
      raw: m,
    }),
  );

  const errors = (Array.isArray(check?.Errors) ? check.Errors : []).map(
    (m) => ({
      severity: 'Error',
      text: toText(m),
      type: m?.error_type || 'Error',
      typeLabel: humanizeType(m?.error_type || 'Error'),
      deviceIds: m?.device_ids ?? [],
      raw: m,
    }),
  );

  // keep the previous order: warnings first, then errors
  return [...warnings, ...errors];
}

/** Collect status candidates for a check (Status plus maybe derived from messages if needed) */
function collectStatusesForCheck(check) {
  const out = [];
  if (check?.Status) out.push(check.Status);
  // fallback: if no Status but errors/warnings exist, derive
  if (!check?.Status) {
    const hasErrors = Array.isArray(check?.Errors) && check.Errors.length > 0;
    const hasWarnings =
      Array.isArray(check?.Warnings) && check.Warnings.length > 0;
    if (hasErrors) out.push('Error');
    else if (hasWarnings) out.push('Warning');
  }
  return out.length ? out : ['Unknown'];
}

/** Build the visual bundle for a single check */
function buildCheckVisual(qos, key) {
  const check = getCheckByName(qos, key);
  if (!check) return null;

  const statuses = collectStatusesForCheck(check);
  const status = worstStatus(statuses);
  const { Color, StatusIcon } = getStatusIcon(status);
  const { label, Icon } = CHECK_ICON_MAP[key] ?? {
    label: key,
    Icon: HelpOutlineRoundedIcon,
  };
  const messages = collectMessages(check);

  return { key, label, Icon, StatusIcon, Color, messages, raw: check, status };
}

export default function EnergyXplorerStatusCell({
  qos,
  row,
  hideSuccess = true,
  size = 'small',
  spacing = 1.0,
}) {
  const { t } = useTranslation('service-management');
  const [openKey, setOpenKey] = React.useState(null);

  if (!qos) return null;

  // derive visuals for all known checks
  const visuals = Object.keys(CHECK_ICON_MAP)
    .map((k) => buildCheckVisual(qos, k))
    .filter(Boolean);

  // optionally hide Success
  const visible = hideSuccess
    ? visuals.filter((v) => v.status !== 'Success')
    : visuals;

  if (visible.length === 0) {
    // if everything is green, show nothing (compact)
    return null;
  }

  const activeVisual = visible.find((v) => v.key === openKey);
  const activeMessages = activeVisual ? activeVisual.messages : [];
  const activeStatus = activeVisual ? activeVisual.status : 'Unknown';
  const { StatusIcon: ActiveStatusIcon, Color: ActiveColor } =
    getStatusIcon(activeStatus);
  const ActiveIcon = activeVisual
    ? (CHECK_ICON_MAP[activeVisual.key]?.Icon ?? HelpOutlineRoundedIcon)
    : null;

  return (
    <>
      {/* icon row */}
      <Stack direction="row" spacing={spacing} alignItems="center">
        {visible.map(({ key, label, Icon, StatusIcon, Color }) => (
          <Tooltip title={label} key={key} arrow placement="top">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <StatusIcon
                  sx={{
                    fontSize: 14,
                    color: (theme) =>
                      Color === 'default'
                        ? theme.palette.text.disabled
                        : theme.palette[Color].main,
                  }}
                />
              }
            >
              <IconButton
                size={size}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenKey(key);
                }}
                sx={{ border: 1, borderColor: 'divider' }}
                aria-label={key}
              >
                <Icon />
              </IconButton>
            </Badge>
          </Tooltip>
        ))}
      </Stack>

      {/* drawer */}
      <InsetDrawer
        open={Boolean(openKey)}
        onClose={() => setOpenKey(null)}
        title={
          activeVisual && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <ActiveStatusIcon
                    sx={{
                      fontSize: 14,
                      color: (theme) =>
                        ActiveColor === 'default'
                          ? theme.palette.text.disabled
                          : theme.palette[ActiveColor].main,
                    }}
                  />
                }
              >
                <IconButton
                  size={size}
                  disabled={true}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    ':disabled': { color: 'text.secondary' },
                  }}
                  aria-label={activeVisual.key}
                >
                  <ActiveIcon />
                </IconButton>
              </Badge>
              <ListItemText
                primary={activeVisual.label}
                secondary={`${row.name} (${row.Account})`}
              />
            </Stack>
          )
        }
        width={800}
      >
        {activeVisual && (
          <>
            {activeMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('energyxplorer.no_details_available')}
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {activeMessages.map((m, i) => {
                  const { StatusIcon, Color } = getStatusIcon(m.severity);
                  return (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                      borderBottom={
                        i < activeMessages.length - 1 ? '1px solid' : 'none'
                      }
                      borderColor="divider"
                      pb={1}
                    >
                      <ListItem>
                        <ListItemIcon>
                          <StatusIcon
                            sx={{
                              color: (theme) =>
                                Color === 'default'
                                  ? theme.palette.text.disabled
                                  : theme.palette[Color].main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            m.typeLabel || m.type || t('energyxplorer.message')
                          }
                          secondary={m.text}
                        />
                      </ListItem>
                    </Stack>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </InsetDrawer>
    </>
  );
}
